import { Inject, Injectable, InjectionToken, NgZone, Optional } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { dismissToast, showToast, type ToastOptions, type ToastStatus } from '@payhoa/ui-kit/elements';
import { ValidationMessageService } from '../../../services/validation-message.service';

export interface GrowlerAction
{
    label: string;
    route: string;
}

export interface GrowlerOptions {
    action?: GrowlerAction;
    /** Milliseconds; `0` stays until dismissed. Unset: a growl with an action stays, the rest the kit times by status and length. */
    autoDismissAfter?: number;
}

export enum GrowlerType {
    DEFAULT = 'default',
    INFO = 'info',
    SUCCESS = 'success',
    WAIT = 'wait',
    ERROR = 'error',
    WARNING = 'warning',
}

/** Where growls go: the kit's toast stack (`ph-toaster`). A spec swaps in a spy. */
export interface GrowlerSink {
    show(options: ToastOptions): string;
    dismiss(id?: string): void;
}

export const GROWLER_SINK = new InjectionToken<GrowlerSink>('GROWLER_SINK', {
    providedIn: 'root',
    factory: () => ({ show: showToast, dismiss: dismissToast }),
});

const STATUS: Record<GrowlerType, ToastStatus> = {
    [GrowlerType.DEFAULT]: 'information',
    [GrowlerType.INFO]: 'information',
    [GrowlerType.WAIT]: 'information',
    [GrowlerType.SUCCESS]: 'success',
    [GrowlerType.ERROR]: 'error',
    [GrowlerType.WARNING]: 'warning',
};

@Injectable({
    providedIn: 'root',
})
export class GrowlerService
{
    constructor(
            @Inject(GROWLER_SINK) private _sink: GrowlerSink,
            private _validationMessageService: ValidationMessageService,
            @Optional() private _router?: Router,
            @Optional() private _zone?: NgZone,
    ) {
    }

    /**
     * @param title
     * @param message
     */
    public success(title: string, message: string, options?: GrowlerOptions): void {
        this.growlMessage(title, message, GrowlerType.SUCCESS, options);
    }

    /**
     * @param title
     * @param message
     */
    public error(title: string, message: string, options?: GrowlerOptions): void {
        this.growlMessage(title, message, GrowlerType.ERROR, options);
    }

    public fromErrorResponse(
            response: HttpErrorResponse,
            defaultMessage = 'An unexpected error occurred. Please try again later.',
    ): void {
        const message = this._validationMessageService.extractResponseErrorText(response, defaultMessage);
        this.oops(message);
    }

    /**
     * @param message
     */
    public oops(message: string): void {
        this.growlMessage('Oops!', message, GrowlerType.ERROR);
    }

    /**
     * @param title
     * @param message
     */
    public warning(title: string, message: string): void {
        this.growlMessage(title, message, GrowlerType.WARNING);
    }

    /**
     * Hand the growl to the kit's toast stack
     * @param title
     * @param message
     * @param type
     */
    private growlMessage(title: string, message: string, type: GrowlerType, options?: GrowlerOptions): void {
        const action = options?.action;
        const id: string = this._sink.show({
            status: STATUS[type],
            title,
            message,
            // A growl with a link stays until dismissed, so the link is not lost to a timer.
            duration: options?.autoDismissAfter ?? (action ? 0 : undefined),
            action: action && {
                label: action.label,
                href: action.route,
                onClick: (event: MouseEvent) => {
                    // A Cmd/Ctrl/Shift/Alt or middle click is the browser's: it opens the link its own way.
                    const modified = event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
                    if (this._router && !modified && !event.defaultPrevented) {
                        event.preventDefault();
                        const navigate = () => this._router.navigateByUrl(action.route);
                        this._zone ? this._zone.run(navigate) : navigate();
                    }
                    this._sink.dismiss(id);
                },
            },
        });
    }
}
